<template>
  <div class="promotion-banner" :class="classes">
    <Media cover :images="images" class="promotion-banner__media" />
    <div class="promotion-banner__content">
      <h2 class="promotion-banner__title">{{ title }}</h2>
      <p v-if="!external" class="promotion-banner__description">
        {{ description }}
      </p>
      <Button
        tag="a"
        :href="buttonUrl"
        form="underline"
        class="promotion-banner__link"
      >
        {{ buttonText }}
      </Button>
    </div>
  </div>
</template>

<script>
import Media from "@/components/Media.vue";
import Button from "@/components/ButtonV2.vue";

export default {
  name: "PromotionBanner",
  components: {
    Media,
    Button,
  },
  props: {
    images: {
      type: Object,
      default: () => ({}),
    },
    video: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    buttonUrl: {
      type: String,
      default: null,
    },
    buttonText: {
      type: String,
      default: null,
    },
    verticalAlign: {
      type: String,
      default: "bottom",
      validator: (value) => {
        return ["middle", "bottom"].includes(value);
      },
    },
    horizontalAlign: {
      type: String,
      default: "center",
      validator: (value) => {
        return ["left", "center"].includes(value);
      },
    },
    external: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String,
      default: "landscape",
      validator(value) {
        return ["landscape", "portrait"].includes(value);
      },
    },
  },
  computed: {
    classes() {
      return {
        "promotion-banner--middle": this.verticalAlign === "middle",
        "promotion-banner--left": this.horizontalAlign === "left",
        "promotion-banner--external": this.external,
        "promotion-banner--portrait": this.orientation === "portrait",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
$landscape-aspect-ratio: calc(((611.25 * 2) + 20) / 764.06);
$portrait-aspect-ratio: calc(291 / 436);

.promotion-banner {
  color: $color-white;
  position: relative;
  aspect-ratio: $landscape-aspect-ratio;
  overflow: hidden;

  &--middle {
    .promotion-banner__content {
      justify-content: center;
    }
  }

  &--left {
    .promotion-banner__content {
      align-items: flex-start;
      text-align: left;
    }
  }

  &--external {
    aspect-ratio: auto;

    &.promotion-banner--portrait {
      aspect-ratio: auto;
    }

    .promotion-banner__content {
      position: static;
      padding: 14px 0;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      height: auto;
      width: auto;
    }

    .promotion-banner__title {
      @include editorial-headline(16px);
      margin-bottom: 0;
      color: $color-black;
      text-align: left;
    }

    .promotion-banner__link {
      text-align: right;
    }
  }

  &--portrait {
    aspect-ratio: $portrait-aspect-ratio;

    .promotion-banner__media {
      aspect-ratio: $portrait-aspect-ratio;
    }
  }

  &__content {
    padding: 40px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    height: 100%;
    width: 100%;
    text-align: center;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
  }

  &__title {
    @include editorial-headline(40px);
    margin-bottom: 8px;
  }

  &__description {
    @include editorial-body(16px);
    margin-bottom: 16px;
  }

  &__media {
    aspect-ratio: $landscape-aspect-ratio;
  }
}
</style>
